import { getCurrentMatchesGradient } from "@/helper/color";
import { translateScheduleLabel } from "@/helper/scheduleLabel";
import { localePath } from "@/helper/urlHelper";
import { getDateWithDay, getMatchTimeLabel } from "@/helper/time";
import { getSeasonString } from "@/helper/util";
import { SeasonTeam } from "@/models/Team";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  MatchWithTeamsAndVenueAndGameday,
  MatchWithTeamsAndVenueAndGamedayAndSeason,
} from "../../models/Match";
import { TeamImage } from "../common/Images";
import { NoItems } from "../common/NoItems";

interface GamescheduleMatchListProps {
  matches: MatchWithTeamsAndVenueAndGamedayAndSeason[];
  baseUrl?: string;
  disableLinks?: boolean;
  openInNewTab?: boolean;
  documentNavigation?: boolean;
}
export function GamescheduleMatchList({
  matches,
  baseUrl,
  disableLinks = false,
  openInNewTab = false,
  documentNavigation = false,
}: GamescheduleMatchListProps) {
  const { t } = useTranslation();
  if (matches.length === 0)
    return (
      <div className="lg:rounded-md lg:w-full lg:py-10 lg:px-8 lg:bg-white lg:text-center lg:ml-0">
        <NoItems message={t("No_Gameday_Data") as string} />
      </div>
    );

  return (
    <>
      {matches.map((match) => (
        <GamescheduleMatchCard
          key={match.id}
          match={match}
          baseUrl={baseUrl}
          disableLinks={disableLinks}
          openInNewTab={openInNewTab}
          documentNavigation={documentNavigation}
        />
      ))}
    </>
  );
}

interface GamescheduleMatchCardProps {
  match: MatchWithTeamsAndVenueAndGamedayAndSeason;
  baseUrl?: string;
  disableLinks?: boolean;
  openInNewTab?: boolean;
  documentNavigation?: boolean;
}
function GamescheduleMatchCard({
  match,
  baseUrl,
  disableLinks = false,
  openInNewTab = false,
  documentNavigation = false,
}: GamescheduleMatchCardProps) {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const timeSuffix = t("Time_Suffix");
  const cardContent = (
    <>
      <div
        className={`absolute left-0 top-0 bottom-0 w-2 h-full bg-gradient-to-b ${getCurrentMatchesGradient(
          match.timeStamp,
          match.matchState,
          match.league
        )}`}
      />
      <div className="w-full">
        <div className="flex flex-col">
          <div className="flex justify-between mb-2">
            <div className="text-sm text-gray-dark">
              {translateScheduleLabel(match.gameday.name, locale)}
            </div>
            <div className="text-sm text-gray-dark uppercase">
              {getDateWithDay(match.timeStamp, locale)}
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <div className={`flex flex-col w-full`}>
            <GamescheduleMatchCardTeam
              team={match.homeTeam.seasonTeam}
              match={match}
            />
            <div className=" bg-gray-light h-px  ml-[58px] my-2" />
            <GamescheduleMatchCardTeam
              team={match.awayTeam.seasonTeam}
              match={match}
              isAway
            />
          </div>
          {match.matchState === "Inactive" && (
            <div
              className={`self-center pl-4 font-antonio font-bold text-xl text-center text-transparent bg-clip-text bg-gradient-to-b ${getCurrentMatchesGradient(
                match.timeStamp,
                match.matchState,
                match.league
              )}`}
            >
              {getMatchTimeLabel(match, locale)}
              {!match.isTimeToBeDefined && timeSuffix ? (
                <>
                  <br />
                  {timeSuffix}
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (disableLinks) {
    return (
      <div className="flex relative items-center mb-1 bg-white p-4 pl-6">
        {cardContent}
      </div>
    );
  }

  const matchHref = `${baseUrl ? baseUrl : ""}/${
    match.league === "Bundesliga" ? "bundesliga" : "pokal"
  }/gameday/${getSeasonString(match.season)}/${match.gameday.index}/${
    match.id
  }`;
  const className = "flex relative items-center mb-1 bg-white p-4 pl-6";
  const target = openInNewTab ? "_blank" : undefined;
  const rel = openInNewTab ? "noopener noreferrer" : undefined;
  // Team detail embeds this list. A client transition from /en keeps t() on de.
  if (documentNavigation && matchHref.startsWith("/")) {
    return (
      <a
        href={localePath(locale, matchHref)}
        className={className}
        target={target}
        rel={rel}
      >
        {cardContent}
      </a>
    );
  }

  return (
    <Link
      href={matchHref}
      className={className}
      target={target}
      rel={rel}
    >
      {cardContent}
    </Link>
  );
}

const GamescheduleMatchCardTeam = ({
  team,
  match,
  isAway = false,
}: {
  team: SeasonTeam;
  match: MatchWithTeamsAndVenueAndGameday;
  isAway?: boolean;
}) => {
  const isActive = match.matchState !== "Inactive";
  return (
    <div className="flex items-center space-x-2 w-full lg:space-x-6">
      <TeamImage team={team} size="lg" />

      <div className={`flex-1 font-antonio font-bold text-xl uppercase`}>
        {team.name}
      </div>
      {isActive && (
        <div className="font-antonio font-bold text-xl">
          {isAway ? match.awayGames : match.homeGames}
        </div>
      )}
    </div>
  );
};
